import './broker-form.scss';

import { Box, Stack } from '@mui/material';
import { CreateBrokerField, getCreateBrokerFieldProps } from '@converge/data';
import {
  ListOptions,
  NewFormTextField,
  NewTypeAhead,
  PhoneExtField,
} from '../../../../presentation';
import { UseFormReturn, useFormContext } from 'react-hook-form';
import {
  UserGroup,
  useGetBDMRegion,
  useGetBrokerageOptionsList,
  useGetOfficeList,
  useGetRegion,
  useGetUsers,
} from '@converge/graphql';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CreateBrokerFields } from './broker-tabs';
import { getUserOptions } from '../../../submission';

export interface BrokerFormProps {
  setPrimary: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  brokerUuid: string | undefined;
  primary: boolean | undefined;
  isReadOnly?: boolean;
  isDisabledPrimary: boolean | undefined;
  setIsDisabledPrimary: React.Dispatch<React.SetStateAction<boolean>>;
  formMethods: UseFormReturn<CreateBrokerFields, unknown>;
  setIsUserBdm: React.Dispatch<React.SetStateAction<boolean>>;
  isUserSetBdm: boolean;
  setIsUserUnderwriter?: React.Dispatch<React.SetStateAction<boolean>>;
  isUserUnderwriter: boolean;
}

const BrokerForm = ({
  isReadOnly,
  formMethods,
  setIsUserBdm,
  isUserSetBdm,
  setIsUserUnderwriter,
  isUserUnderwriter,
}: BrokerFormProps) => {
  const navigate = useNavigate();
  const { parentData, loading: isBrokerageOptionsLoading } =
    useGetBrokerageOptionsList();
  const { getValues } = useFormContext();
  const { data: region, loading: isUWRegionsLoading } = useGetRegion();
  const { data: BdmRegion, loading: isBDMRegionsLoading } = useGetBDMRegion();
  const { data: underwriterList, loading: isUnderwriterListLoading } =
    useGetUsers({ userGroups: [UserGroup.Underwriter] });
  const { data: businessDevManager, loading: isBDMListLoading } = useGetUsers({
    userGroups: [UserGroup.Bdm],
  });
  const [bdmTooltip, setBdmTooltip] = useState<React.ReactNode>(undefined);
  const [uwTooltip, setUwTooltip] = useState<React.ReactNode>(undefined);
  const parentBrokerage = getValues(CreateBrokerField.GlobalParent);

  const PARENTS_ENTITY_OPTIONS = parentData
    ?.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
    ?.map((parentOption) => {
      return {
        value: parentOption.value,
        label: parentOption.name ?? 'No Aliases',
        data: parentOption?.data?.node?.uuid,
        underwriterData: parentOption?.underwriterData?.node?.uuid,
      };
    });
  const UNDERWRITING_REGION_OPTIONS = region?.uwTerritories?.edges
    ?.map((parentOption) => {
      return {
        value: parentOption?.node?.uuid || '',
        label: parentOption?.node?.name || '',
      };
    })
    ?.sort((a, b) => a?.label.localeCompare(b?.label));

  const UNDERWRITING_BDM_REGION_OPTIONS = BdmRegion?.bdmTerritories?.edges
    ?.map((bdmRegionOption) => {
      return {
        value: bdmRegionOption?.node?.uuid || '',
        label: bdmRegionOption?.node?.name || '',
      };
    })
    ?.sort((a, b) => a?.label.localeCompare(b?.label));

  const UNDERWRITER_OPTIONS = useMemo(
    () => getUserOptions(underwriterList) ?? [],
    [underwriterList]
  );

  const BUSINESS_DEV_MANAGER_OPTIONS = useMemo(
    () => getUserOptions(businessDevManager) ?? [],
    [businessDevManager]
  );

  const watchedTeamValue = formMethods.watch(CreateBrokerField.BrokerTeam);
  const watchedOfficeValue = formMethods.watch(CreateBrokerField.Office);
  const watchedBrokerageValue = formMethods.watch(
    CreateBrokerField.GlobalParent
  );
  const watchedUWValue = formMethods.watch(CreateBrokerField.Underwriter);
  const watchedBDMValue = formMethods.watch(
    CreateBrokerField.BusinessDevelopmentManager
  );

  const { data: office, loading: isOfficesLoading } = useGetOfficeList({
    brokerageUuid: parentBrokerage ?? '',
    enabled: Boolean(parentBrokerage),
  });

  const OFFICE_OPTIONS = office?.entities?.edges[0]?.node?.offices?.edges
    ?.map((officeOption) => {
      const bdmUser = officeOption?.node?.users?.edges?.find(
        (user) => user?.node?.type === UserGroup.Bdm
      );
      const underwriterUser = officeOption?.node?.users?.edges?.find(
        (user) => user?.node?.type === UserGroup.Underwriter
      );
      return {
        value: officeOption.node.uuid ?? null,
        label: officeOption.node.name ?? 'No Aliases',
        data: bdmUser?.node?.uuid,
        underwriterData: underwriterUser?.node?.uuid,
      };
    })
    ?.sort((a, b) => a?.label.localeCompare(b?.label));

  const TEAM_OPTIONS = office?.entities?.edges[0]?.node?.teams?.edges
    ?.map((teamOption) => {
      const bdmUser = teamOption?.node?.users?.edges?.find(
        (user) => user?.node?.type === UserGroup.Bdm
      );
      const underwriterUser = teamOption?.node?.users?.edges?.find(
        (user) => user?.node?.type === UserGroup.Underwriter
      );

      return {
        value: teamOption.node.uuid ?? null,
        label: teamOption.node.name ?? 'No Aliases',
        data: bdmUser?.node?.uuid,
        underwriterData: underwriterUser?.node?.uuid,
      };
    })
    ?.sort((a, b) => a?.label.localeCompare(b?.label));

  const handleEntityChange = useMemo(
    () =>
      (
        selectedOption: ListOptions<
          {
            value: string | null;
            label: string;
            data: string | null | undefined;
            underwriterData?: string | null | undefined;
          },
          string
        > | null,
        entityType: 'team' | 'office' | 'brokerage'
      ) => {
        const teamValue = formMethods.getValues(CreateBrokerField.BrokerTeam);
        const officeValue = formMethods.getValues(CreateBrokerField.Office);
        const brokerageValue = formMethods.getValues(
          CreateBrokerField.GlobalParent
        );

        const setBdmValue = (value: string) => {
          formMethods.setValue(
            CreateBrokerField.BusinessDevelopmentManager,
            value
          );
        };

        const setUnderwriterValue = (value: string) => {
          formMethods.setValue(CreateBrokerField.Underwriter, value);
        };

        if (isUserSetBdm && isUserUnderwriter) return;

        // Early return for BDM if manually set
        const shouldSkipBdmUpdate = isUserSetBdm;

        // Early return for Underwriter if manually set
        const shouldSkipUnderwriterUpdate = isUserUnderwriter;

        const getTeamBdm = () =>
          TEAM_OPTIONS?.find((team) => team.value === teamValue)?.data;

        const getTeamUnderwriter = () =>
          TEAM_OPTIONS?.find((team) => team.value === teamValue)
            ?.underwriterData;

        const getOfficeBdm = () =>
          OFFICE_OPTIONS?.find((office) => office.value === officeValue)
            ?.data || '';

        const getOfficeUnderwriter = () =>
          OFFICE_OPTIONS?.find((office) => office.value === officeValue)
            ?.underwriterData || '';

        const getBrokerageBdm = () =>
          PARENTS_ENTITY_OPTIONS?.find(
            (parent) => parent.value === brokerageValue
          )?.data || '';

        const getBrokerageUnderwriter = () =>
          PARENTS_ENTITY_OPTIONS?.find(
            (parent) => parent.value === brokerageValue
          )?.underwriterData || '';

        switch (entityType) {
          case 'team':
            if (!shouldSkipBdmUpdate) {
              if (selectedOption?.data) {
                setBdmValue(selectedOption.data.toString());
              } else {
                // If team BDM is removed, cascade to office BDM
                const officeBdm = getOfficeBdm();
                if (officeBdm) {
                  setBdmValue(officeBdm);
                } else {
                  // If office has no BDM, use brokerage BDM
                  setBdmValue(getBrokerageBdm());
                }
              }
            }

            if (!shouldSkipUnderwriterUpdate) {
              const option = selectedOption as unknown as {
                underwriterData?: string | null | undefined;
              };
              if (option?.underwriterData) {
                setUnderwriterValue(option.underwriterData.toString());
              } else {
                const officeUnderwriter = getOfficeUnderwriter();
                if (officeUnderwriter) {
                  setUnderwriterValue(officeUnderwriter);
                } else {
                  // If office has no underwriter, use brokerage underwriter
                  setUnderwriterValue(getBrokerageUnderwriter());
                }
              }
            }
            break;

          case 'office': {
            if (!shouldSkipBdmUpdate) {
              // First check if team has a BDM
              const teamBdm = getTeamBdm();

              if (teamBdm) {
                // Team BDM has highest priority
                setBdmValue(teamBdm.toString());
              } else if (selectedOption?.data) {
                // If team has no BDM but office has, use office BDM
                setBdmValue(selectedOption.data.toString());
              } else {
                // If neither team nor selected office has BDM, check brokerage
                const brokerageBdm = getBrokerageBdm();
                if (brokerageBdm) {
                  setBdmValue(brokerageBdm);
                }
              }
            }

            if (!shouldSkipUnderwriterUpdate) {
              const teamUnderwriter = getTeamUnderwriter();
              const option = selectedOption as unknown as {
                underwriterData?: string | null | undefined;
              };

              // Cascading priority: Team > Selected Office > Brokerage
              if (teamUnderwriter) {
                setUnderwriterValue(teamUnderwriter.toString());
              } else if (option?.underwriterData) {
                setUnderwriterValue(option.underwriterData.toString());
              } else {
                const brokerageUnderwriter = getBrokerageUnderwriter();
                if (brokerageUnderwriter) {
                  setUnderwriterValue(brokerageUnderwriter);
                }
              }
            }
            break;
          }

          case 'brokerage': {
            if (!shouldSkipBdmUpdate) {
              // First check for team BDM
              const teamBdm = getTeamBdm();
              if (teamBdm) {
                // Team BDM has highest priority
                setBdmValue(teamBdm.toString());
                return;
              }

              // Then check for office BDM
              const officeBdm = getOfficeBdm();
              if (officeBdm) {
                // Office BDM has second priority
                setBdmValue(officeBdm.toString());
                return;
              }

              // Finally, use selected brokerage BDM
              if (selectedOption?.data) {
                setBdmValue(selectedOption.data.toString());
              }
            }

            if (!shouldSkipUnderwriterUpdate) {
              const teamUnderwriter = getTeamUnderwriter();
              const officeUnderwriter = getOfficeUnderwriter();
              const option = selectedOption as unknown as {
                underwriterData?: string | null | undefined;
              };

              // Cascading priority: Team > Office > Selected Brokerage
              if (teamUnderwriter) {
                setUnderwriterValue(teamUnderwriter.toString());
              } else if (officeUnderwriter) {
                setUnderwriterValue(officeUnderwriter.toString());
              } else if (option?.underwriterData) {
                setUnderwriterValue(option.underwriterData.toString());
              }
            }
            break;
          }
        }
      },
    [
      isUserSetBdm,
      isUserUnderwriter,
      formMethods,
      TEAM_OPTIONS,
      OFFICE_OPTIONS,
      PARENTS_ENTITY_OPTIONS,
    ]
  );

  // Apply the cascading logic for underwriter when the form loads or when dependencies change
  useEffect(() => {
    // Skip if user has manually set the underwriter
    if (isUserUnderwriter) return;

    const teamValue = formMethods.getValues(CreateBrokerField.BrokerTeam);
    const officeValue = formMethods.getValues(CreateBrokerField.Office);
    const brokerageValue = formMethods.getValues(
      CreateBrokerField.GlobalParent
    );

    // First check if broker already has an underwriter
    const brokerValue = formMethods.getValues(CreateBrokerField.Underwriter);
    if (brokerValue) return;

    // Check team underwriter
    if (teamValue) {
      const teamUnderwriter = TEAM_OPTIONS?.find(
        (team) => team.value === teamValue
      )?.underwriterData;
      if (teamUnderwriter) {
        formMethods.setValue(CreateBrokerField.Underwriter, teamUnderwriter);
        return;
      }
    }

    // Check office underwriter
    if (officeValue) {
      const officeUnderwriter = OFFICE_OPTIONS?.find(
        (office) => office.value === officeValue
      )?.underwriterData;
      if (officeUnderwriter) {
        formMethods.setValue(CreateBrokerField.Underwriter, officeUnderwriter);
        return;
      }
    }

    // Check brokerage underwriter
    if (brokerageValue) {
      const brokerageUnderwriter = PARENTS_ENTITY_OPTIONS?.find(
        (parent) => parent.value === brokerageValue
      )?.underwriterData;
      if (brokerageUnderwriter) {
        formMethods.setValue(
          CreateBrokerField.Underwriter,
          brokerageUnderwriter
        );
        return;
      }
    }
  }, [
    formMethods,
    TEAM_OPTIONS,
    OFFICE_OPTIONS,
    PARENTS_ENTITY_OPTIONS,
    isUserUnderwriter,
  ]);

  // Apply the cascading logic for BDM when the form loads or when dependencies change
  useEffect(() => {
    // Skip if user has manually set the BDM
    if (isUserSetBdm) return;

    const teamValue = formMethods.getValues(CreateBrokerField.BrokerTeam);
    const officeValue = formMethods.getValues(CreateBrokerField.Office);
    const brokerageValue = formMethods.getValues(
      CreateBrokerField.GlobalParent
    );

    // First check if broker already has a BDM
    const bdmValue = formMethods.getValues(
      CreateBrokerField.BusinessDevelopmentManager
    );
    if (bdmValue) return;

    // Check team BDM
    if (teamValue) {
      const teamBdm = TEAM_OPTIONS?.find(
        (team) => team.value === teamValue
      )?.data;
      if (teamBdm) {
        formMethods.setValue(
          CreateBrokerField.BusinessDevelopmentManager,
          teamBdm
        );
        return;
      }
    }

    // Check office BDM
    if (officeValue) {
      const officeBdm = OFFICE_OPTIONS?.find(
        (office) => office.value === officeValue
      )?.data;
      if (officeBdm) {
        formMethods.setValue(
          CreateBrokerField.BusinessDevelopmentManager,
          officeBdm
        );
        return;
      }
    }

    // Check brokerage BDM
    if (brokerageValue) {
      const brokerageBdm = PARENTS_ENTITY_OPTIONS?.find(
        (parent) => parent.value === brokerageValue
      )?.data;
      if (brokerageBdm) {
        formMethods.setValue(
          CreateBrokerField.BusinessDevelopmentManager,
          brokerageBdm
        );
        return;
      }
    }
  }, [
    formMethods,
    TEAM_OPTIONS,
    OFFICE_OPTIONS,
    PARENTS_ENTITY_OPTIONS,
    isUserSetBdm,
  ]);

  useEffect(() => {
    if (isUserSetBdm) {
      setBdmTooltip(
        'This Business Development Manager has been manually selected.'
      );
      return;
    }

    const teamValue = watchedTeamValue;
    const officeValue = watchedOfficeValue;
    const brokerageValue = watchedBrokerageValue;

    const teamOption = TEAM_OPTIONS?.find((t) => t.value === teamValue);
    if (teamValue && teamOption?.data) {
      setBdmTooltip(
        <span>
          This Business Development Manager has been inherited from the{' '}
          <span
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(
                `/producers/brokerage/${brokerageValue}/details?section=team`
              );
            }}
            style={{
              textDecoration: 'underline',
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            {teamOption.label}
          </span>{' '}
          team.
        </span>
      );
      return;
    }

    const officeOption = OFFICE_OPTIONS?.find((o) => o.value === officeValue);
    if (officeValue && officeOption?.data) {
      setBdmTooltip(
        <span>
          This Business Development Manager has been inherited from the{' '}
          <span
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(
                `/producers/brokerage/${brokerageValue}/details?section=office`
              );
            }}
            style={{
              textDecoration: 'underline',
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            {officeOption.label}
          </span>{' '}
          office.
        </span>
      );
      return;
    }

    const brokerageOption = PARENTS_ENTITY_OPTIONS?.find(
      (p) => p.value === brokerageValue
    );
    if (brokerageValue && brokerageOption?.data) {
      setBdmTooltip(
        <span>
          This Business Development Manager has been inherited from the{' '}
          <span
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`/producers/brokerage/${brokerageValue}/details`);
            }}
            style={{
              textDecoration: 'underline',
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            {brokerageOption.label}
          </span>{' '}
          brokerage.
        </span>
      );
      return;
    }

    if (watchedBDMValue) {
      setBdmTooltip(
        'This Business Development Manager has been manually selected.'
      );
      return;
    }

    setBdmTooltip(undefined);
  }, [
    isUserSetBdm,
    watchedTeamValue,
    watchedOfficeValue,
    watchedBrokerageValue,
    watchedBDMValue,
    TEAM_OPTIONS,
    OFFICE_OPTIONS,
    PARENTS_ENTITY_OPTIONS,
    navigate,
  ]);

  useEffect(() => {
    if (isUserUnderwriter) {
      setUwTooltip('This Underwriter has been manually selected.');
      return;
    }

    const teamValue = watchedTeamValue;
    const officeValue = watchedOfficeValue;
    const brokerageValue = watchedBrokerageValue;

    const teamOption = TEAM_OPTIONS?.find((t) => t.value === teamValue);
    if (teamValue && teamOption?.underwriterData) {
      setUwTooltip(
        <span>
          This Underwriter has been inherited from the{' '}
          <span
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(
                `/producers/brokerage/${brokerageValue}/details?section=team`
              );
            }}
            style={{
              textDecoration: 'underline',
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            {teamOption.label}
          </span>{' '}
          team.
        </span>
      );
      return;
    }

    const officeOption = OFFICE_OPTIONS?.find((o) => o.value === officeValue);
    if (officeValue && officeOption?.underwriterData) {
      setUwTooltip(
        <span>
          This Underwriter has been inherited from the{' '}
          <span
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(
                `/producers/brokerage/${brokerageValue}/details?section=office`
              );
            }}
            style={{
              textDecoration: 'underline',
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            {officeOption.label}
          </span>{' '}
          office.
        </span>
      );
      return;
    }

    const brokerageOption = PARENTS_ENTITY_OPTIONS?.find(
      (p) => p.value === brokerageValue
    );
    if (brokerageValue && brokerageOption?.underwriterData) {
      setUwTooltip(
        <span>
          This Underwriter has been inherited from the{' '}
          <span
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`/producers/brokerage/${brokerageValue}/details`);
            }}
            style={{
              textDecoration: 'underline',
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            {brokerageOption.label}
          </span>{' '}
          brokerage.
        </span>
      );
      return;
    }

    if (watchedUWValue) {
      setUwTooltip('This Underwriter has been manually selected.');
      return;
    }

    setUwTooltip(undefined);
  }, [
    isUserUnderwriter,
    watchedTeamValue,
    watchedOfficeValue,
    watchedBrokerageValue,
    watchedUWValue,
    TEAM_OPTIONS,
    OFFICE_OPTIONS,
    PARENTS_ENTITY_OPTIONS,
    navigate,
  ]);

  return (
    <>
      <div>
        <Stack className="broker-row-uw">
          <Box className="broker-business-manager">
            <NewTypeAhead
              {...getCreateBrokerFieldProps(
                CreateBrokerField.BusinessDevelopmentManager
              )}
              placeholder="Select BDM"
              dataTestId="bdm-dropdown"
              options={BUSINESS_DEV_MANAGER_OPTIONS || []}
              disabled={isReadOnly}
              onChangeOption={(e) => {
                if (e?.value === undefined) {
                  // When BDM is cleared, mark as not user-selected
                  setIsUserBdm(false);
                } else if (e?.value) {
                  // When BDM is manually selected, mark as user-selected
                  setIsUserBdm(true);
                }
              }}
              isLoading={isBDMListLoading}
              tooltip={bdmTooltip}
            />
          </Box>
          <Box className="bdm-region">
            <NewTypeAhead
              {...getCreateBrokerFieldProps(CreateBrokerField.BdmRegion)}
              disabled={isReadOnly}
              options={UNDERWRITING_BDM_REGION_OPTIONS || []}
              placeholder="Select BDM Region"
              dataTestId="bdm-region-dropdown"
              isLoading={isBDMRegionsLoading}
            />
          </Box>
          <Box className="broker-underwriter">
            <NewTypeAhead
              {...getCreateBrokerFieldProps(CreateBrokerField.Underwriter)}
              disabled={isReadOnly}
              placeholder="Select UW"
              dataTestId="underwriter-dropdown"
              options={UNDERWRITER_OPTIONS || []}
              isLoading={isUnderwriterListLoading}
              onChangeOption={(e) => {
                if (setIsUserUnderwriter) {
                  if (e?.value === undefined) {
                    // Only reset isUserUnderwriter when explicitly cleared
                    setIsUserUnderwriter(false);
                  } else if (e?.value) {
                    // Mark as user-selected when explicitly selected
                    setIsUserUnderwriter(true);
                  }
                }
              }}
              tooltip={uwTooltip}
            />
          </Box>
          <Box className="broker-uw-region">
            <NewTypeAhead
              {...getCreateBrokerFieldProps(CreateBrokerField.UwRegion)}
              disabled={isReadOnly}
              options={UNDERWRITING_REGION_OPTIONS || []}
              placeholder="Select UW Region"
              dataTestId="uw-region-dropdown"
              isLoading={isUWRegionsLoading}
            />
          </Box>
        </Stack>
        <Stack className="broker-row-three-column no-border">
          <Box className="broker-field-three-column">
            <NewFormTextField
              {...getCreateBrokerFieldProps(CreateBrokerField.FirstName)}
              required
              autoComplete={false}
              dataTestId="first-name-input"
              placeholder="Enter First Name"
              error
              disabled={isReadOnly}
            />
          </Box>
          <Box className="broker-field-three-column">
            <NewFormTextField
              {...getCreateBrokerFieldProps(CreateBrokerField.LastName)}
              required
              dataTestId="last-name-input"
              autoComplete={false}
              placeholder="Enter Last Name"
              error
              disabled={isReadOnly}
            />
          </Box>
          <Box className="broker-field-three-column">
            <NewFormTextField
              {...getCreateBrokerFieldProps(CreateBrokerField.BrokerTitle)}
              dataTestId="broker-title-input"
              autoComplete={false}
              placeholder="Enter Broker Title"
              error
              disabled={isReadOnly}
            />
          </Box>
        </Stack>
        <Stack className="broker-row-three-column ">
          <Box className="broker-field-three-column">
            <NewFormTextField
              {...getCreateBrokerFieldProps(CreateBrokerField.Email)}
              placeholder="Enter name@email.com"
              required
              dataTestId="primary-email-input"
              disabled={isReadOnly}
            />
          </Box>
          <Box className="broker-field-three-column">
            <NewFormTextField
              {...getCreateBrokerFieldProps(CreateBrokerField.SecondaryEmail)}
              placeholder="Enter name@email.com"
              dataTestId="secondary-email-input"
              disabled={isReadOnly}
            />
          </Box>
          <Box className="broker-field-three-column">
            <NewFormTextField
              {...getCreateBrokerFieldProps(CreateBrokerField.TertiaryEmail)}
              placeholder="Enter name@email.com"
              dataTestId="tertiary-email-input"
              disabled={isReadOnly}
            />
          </Box>
        </Stack>
        <Stack className="broker-row">
          <Box className="parent-brokerage">
            <NewTypeAhead
              {...getCreateBrokerFieldProps(CreateBrokerField.GlobalParent)}
              placeholder="Select Brokerage"
              dataTestId="brokerage-dropdown"
              options={PARENTS_ENTITY_OPTIONS || []}
              required
              disabled={isReadOnly}
              isLoading={isBrokerageOptionsLoading}
              onChangeOption={(e) => handleEntityChange(e, 'brokerage')}
            />
          </Box>

          <Box className="Brokerage-subsidiary">
            <NewTypeAhead
              {...getCreateBrokerFieldProps(CreateBrokerField.Office)}
              disabled={!parentBrokerage || isReadOnly}
              placeholder="Select Office"
              dataTestId="office-dropdown"
              options={OFFICE_OPTIONS || []}
              isLoading={isOfficesLoading}
              onChangeOption={(e) => handleEntityChange(e, 'office')}
            />
          </Box>
          <Box className="broker-team" data-testid="broker-broker-team">
            <NewTypeAhead
              {...getCreateBrokerFieldProps(CreateBrokerField.BrokerTeam)}
              disabled={!parentBrokerage || isReadOnly}
              options={TEAM_OPTIONS || []}
              placeholder="Select Broker Team"
              dataTestId="broker-team-dropdown"
              onChangeOption={(e) => handleEntityChange(e, 'team')}
            />
          </Box>
        </Stack>
        <Stack className="broker-row">
          <PhoneExtField
            disabled={isReadOnly}
            dataTestId="mobile-phone-input"
            placeholder="415-555-1212"
            className="mobile-phone"
            mainClassName="primary-phone"
            autoComplete={false}
            dataExtTestId="mobile-phone-extension-input"
            classNameExt="mobile-phone-extn"
            fieldPropsIdx={CreateBrokerField.MobilePhoneNumber}
            fieldPropsExtIdx={CreateBrokerField.MobilePhoneNumberExtn}
          />

          <PhoneExtField
            disabled={isReadOnly}
            dataTestId="office-phone-input"
            placeholder="415-555-1212"
            className="phone-field"
            mainClassName="primary-phone"
            autoComplete={false}
            dataExtTestId="office-phone-extension-input"
            classNameExt="mobile-phone-extn"
            fieldPropsIdx={CreateBrokerField.OfficePhoneNumber}
            fieldPropsExtIdx={CreateBrokerField.OfficePhoneNumberExtn}
          />

          <Box className="npn">
            <NewFormTextField
              {...getCreateBrokerFieldProps(CreateBrokerField.Npn)}
              autoComplete={false}
              placeholder="Enter NPN"
              dataTestId="npn-input"
              isNumber
              disabled={isReadOnly}
            />
          </Box>
        </Stack>
        {/* 
        <Stack className="broker-row-commission">
          <Stack className="brokerage-commission-container">
            <Box className="commission-row">
              {primary === true && (
                <>
                  <img
                    src={config.checkBox}
                    className={`${
                      isDisabledPrimary || isReadOnly
                        ? 'checkbox-disabled check-box-icon'
                        : 'check-box-icon'
                    }`}
                    data-testid="broker-selected-checkBox"
                    onClick={() => {
                      if (isDisabledPrimary || isReadOnly) {
                        setPrimary(true);
                      } else {
                        setPrimary(false);
                      }
                    }}
                    alt="selected checkBox"
                  />
                  <Box
                    className={`${
                      isDisabledPrimary || isReadOnly
                        ? 'checkbox-text disabled'
                        : 'checkbox-text'
                    }`}
                  >
                    Primary broker for selected team
                  </Box>
                </>
              )}
              {(primary === false || primary === undefined) && (
                <>
                  <img
                    src={config.unselectedCheckBox}
                    data-testid="broker-unselected-checkBox"
                    className={`${
                      isDisabledPrimary || isReadOnly
                        ? 'checkbox-disabled check-box-icon'
                        : 'check-box-icon'
                    }`}
                    onClick={() => {
                      if (isDisabledPrimary || isReadOnly) {
                        setPrimary(false);
                      } else {
                        setPrimary(true);
                      }
                    }}
                    alt="unselected checkBox"
                  />
                  <Box
                    className={`${
                      isDisabledPrimary || isReadOnly
                        ? 'checkbox-text disabled'
                        : 'checkbox-text'
                    }`}
                  >
                    Primary broker for selected team
                  </Box>
                </>
              )}
            </Box>
          </Stack>
          <Box className="empty-space-middle"></Box>
          <Box className="empty-space-right"></Box>
        </Stack> */}
      </div>
    </>
  );
};

export default BrokerForm;
